import { Placement } from '@floating-ui/dom';

/**
 * Constantes du mécanisme d'infobulle unique de l'application.
 *
 * Un seul élément `role="tooltip"` vit dans le `<body>` ; il est piloté par
 * {@link TooltipService} (rendu) et alimenté par {@link TooltipDelegateService}
 * (écoute globale du survol et du focus). Les attributs ci-dessous sont le
 * contrat entre les gabarits et ce mécanisme.
 */

/** Texte explicite d'infobulle, posé par la directive `appTooltip`. */
export const TOOLTIP_TEXT_ATTRIBUTE = 'data-tooltip';

/**
 * Valeur machine d'une date avec heure, posée par `appDateTimeTooltip`. Elle
 * n'est mise en forme qu'à l'affichage, dans la langue active À CE MOMENT —
 * un changement de langue n'a donc rien à rafraîchir.
 */
export const TOOLTIP_DATETIME_ATTRIBUTE = 'data-tooltip-datetime';

/**
 * Mémoire d'un `title` natif neutralisé. Le `title` est retiré dès le premier
 * survol : laissé en place, le navigateur afficherait SA bulle, avec son délai,
 * par-dessus la nôtre.
 */
export const TOOLTIP_NEUTRALIZED_TITLE_ATTRIBUTE = 'data-tooltip-title';

/**
 * Marque un `aria-label` posé par le mécanisme pour remplacer le nom
 * accessible que portait un `title` neutralisé sur une icône seule. Il est
 * resynchronisé si le `title` change (ex. verrouiller ↔ déverrouiller).
 */
export const TOOLTIP_DERIVED_LABEL_ATTRIBUTE = 'data-tooltip-derived-label';

/** Attributs dont un changement doit rafraîchir l'infobulle affichée. */
export const TOOLTIP_OBSERVED_ATTRIBUTES: readonly string[] = [
  'title',
  'aria-label',
  TOOLTIP_TEXT_ATTRIBUTE,
  TOOLTIP_DATETIME_ATTRIBUTE,
];

/** Éléments susceptibles de porter une infobulle (filtrés ensuite). */
export const TOOLTIP_CANDIDATE_SELECTOR = [
  `[${TOOLTIP_TEXT_ATTRIBUTE}]`,
  `[${TOOLTIP_DATETIME_ATTRIBUTE}]`,
  '[title]',
  `[${TOOLTIP_NEUTRALIZED_TITLE_ATTRIBUTE}]`,
  '[aria-label]',
].join(', ');

/**
 * Ce qui compte pour une icône. Un bouton dont tout le contenu visible est
 * fait de ces éléments est une « icône seule » : son `aria-label` devient son
 * infobulle.
 */
export const TOOLTIP_ICON_SELECTOR = [
  '.material-symbols-rounded',
  '.material-symbols-outlined',
  '.material-icons',
  'mat-icon',
  'img',
  'svg',
].join(', ');

/** Identifiant DOM de la bulle, cible des `aria-describedby`. */
export const TOOLTIP_ELEMENT_ID = 'app-tooltip';

/** Classe CSS de la bulle (cf. `src/styles/components/tooltip.scss`). */
export const TOOLTIP_CLASS = 'app-tooltip';

/** Côté préféré ; `flip` bascule dessous quand la place manque en haut. */
export const TOOLTIP_PLACEMENT: Placement = 'top';

/** Écart entre l'élément survolé et la bulle. */
export const TOOLTIP_OFFSET_PX = 6;

/** Marge minimale gardée avec les bords de l'écran. */
export const TOOLTIP_VIEWPORT_PADDING_PX = 8;

/** Touche qui ferme l'infobulle (WCAG 1.4.13, contenu « dismissible »). */
export const TOOLTIP_DISMISS_KEY = 'Escape';
